import React, { useEffect, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import {
    Check,
    CheckCircleOutline,
    CreditCardOutlined,
    ErrorOutline,
    GppMaybeOutlined,
    PriorityHigh,
    ReceiptLongOutlined,
    ScheduleOutlined,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { nextSunday } from 'date-fns';
import { Colors } from 'library';
import { Card, Grid, Icon, Link, PillButton, Row, Stack, Text } from 'library/ui';
import { client, consoleLogInDev, download, formatCurrency } from 'shared';
import { formatShortMonthDay, formatShortWeekdayMonthDay } from 'shared/Dates';
import { getPaymentMethods, PaymentMethod } from 'shared/payments';
import { verifyBankAccountMandate } from 'api/PaymentInformationApi';
import { useUserContext } from 'UserContext';

interface LastInvoice {
    id: number;
    amount: number;
    bill_date: string;
    paid_date: string | null;
    is_paid: boolean;
}

interface LastPayment {
    amount: number;
    paid_date: string | null;
}

interface PaymentSummary {
    amount_due: number;
    failed_count: number;
    failed_invoice_ids: number[];
    last_invoice: LastInvoice | null;
    last_payment: LastPayment | null;
    next_due_date: string | null;
    next_due_amount: number;
    has_upcoming_charge: boolean;
    upcoming_charge_amount: number;
}

interface PaymentsSummaryProps {
    refreshKey: number;
    onPaymentsRetried: () => void;
}

const PaymentsSummary = ({ refreshKey, onPaymentsRetried }: PaymentsSummaryProps) => {
    const { user } = useUserContext();
    const isManualPayer = user?.is_manual_payer;
    const [summary, setSummary] = useState<PaymentSummary>();
    const [methods, setMethods] = useState<PaymentMethod[]>([]);
    const [retrying, setRetrying] = useState(false);
    const [consenting, setConsenting] = useState(false);
    const stripe = useStripe();

    useEffect(() => {
        loadSummary();
    }, [refreshKey]);

    useEffect(() => {
        getPaymentMethods().then(setMethods).catch(consoleLogInDev);
    }, []);

    function loadSummary() {
        client('payment/api/invoice/payment-summary/')
            .then((response: PaymentSummary) => setSummary(response))
            .catch(consoleLogInDev);
    }

    if (!summary) {
        return null;
    }

    const {
        amount_due,
        failed_count,
        failed_invoice_ids,
        last_invoice,
        last_payment,
        next_due_date,
        next_due_amount,
        has_upcoming_charge,
        upcoming_charge_amount,
    } = summary;
    const defaultMethod = methods.find((method) => method.is_default);
    const consentMethod = methods.find((method) => method.needs_mandate);

    const today = new Date();
    const nextChargeDate = today.getDay() === 0 ? today : nextSunday(today);
    const localDueDate = next_due_date ? next_due_date.replaceAll('-', '/') : null;
    const nextShort = isManualPayer
        ? localDueDate
            ? formatShortMonthDay(localDueDate)
            : '—'
        : has_upcoming_charge
          ? formatShortMonthDay(nextChargeDate)
          : '—';
    const nextLong = isManualPayer
        ? localDueDate
            ? formatShortWeekdayMonthDay(localDueDate)
            : null
        : has_upcoming_charge
          ? formatShortWeekdayMonthDay(nextChargeDate)
          : null;
    const nextSummary = nextLong ? `${isManualPayer ? 'next due' : 'next charge'} ${nextLong}` : '';

    const showFailedBanner = !isManualPayer && amount_due > 0;
    const showCaughtUpBanner = amount_due === 0 && !!last_invoice?.is_paid;

    function onViewInvoice() {
        if (!last_invoice) return;
        download(`payment/api/invoice/${last_invoice.id}/download/`).catch(consoleLogInDev);
    }

    function onRetryAll() {
        setRetrying(true);
        Promise.allSettled(
            failed_invoice_ids.map((id) =>
                client(`payment/api/invoice/${id}/retry/`, { body: { payment_information_id: defaultMethod?.id } }),
            ),
        )
            .then(onPaymentsRetried)
            .finally(() => setRetrying(false));
    }

    function onConsent() {
        if (!consentMethod) return;
        setConsenting(true);
        verifyBankAccountMandate(stripe, consentMethod.id, user?.customer_id)
            .then(() => getPaymentMethods().then(setMethods))
            .catch(consoleLogInDev)
            .finally(() => setConsenting(false));
    }

    return (
        <Stack gap="md">
            {consentMethod && (
                <Card tone="error" padding="sm">
                    <Row justify="between" align="center" gap="md">
                        <Row gap="md" align="center">
                            <Icon icon={GppMaybeOutlined} size="sm" bgColor={Colors.error} iconColor={Colors.white} />
                            <Stack gap="none">
                                <Text weight="bold" tone="error">
                                    Action required · consent needed
                                </Text>
                                <Text size="sm" tone="secondary">
                                    Re-authorize {consentMethod.bank_name || 'your bank account'} ••{' '}
                                    {consentMethod.last4} to keep your payments running. Without it, payments will fail.
                                </Text>
                            </Stack>
                        </Row>
                        <PillButton disabled={consenting} onClick={onConsent}>
                            {consenting ? 'Submitting...' : 'Agree & Consent'}
                        </PillButton>
                    </Row>
                </Card>
            )}
            {showFailedBanner && (
                <Card tone="warning" padding="sm">
                    <Row justify="between" align="center" gap="md">
                        <Row gap="md" align="center">
                            <Icon icon={PriorityHigh} size="sm" bgColor={Colors.warning} iconColor={Colors.white} />
                            <Stack gap="none">
                                <Text weight="bold" tone="warning">
                                    {failed_count} payment{failed_count === 1 ? '' : 's'} couldn&apos;t be processed
                                </Text>
                                <Text size="sm" tone="secondary">
                                    {formatCurrency(amount_due)} owed. Update your default method or retry the charges
                                    below.
                                </Text>
                            </Stack>
                        </Row>
                        <PillButton color={Colors.warning} disabled={retrying} onClick={onRetryAll}>
                            {retrying ? 'Retrying...' : 'Retry all'}
                        </PillButton>
                    </Row>
                </Card>
            )}
            {showCaughtUpBanner && last_invoice && (
                <Card tone="success" padding="sm">
                    <Row justify="between" align="center" gap="md">
                        <Row gap="md" align="center">
                            <Icon icon={Check} size="md" bgColor={Colors.success} iconColor={Colors.white} />
                            <Stack gap="none">
                                <Text weight="bold">You&apos;re all caught up</Text>
                                <Text size="sm" tone="secondary">
                                    Invoice {last_invoice.id} paid · {formatCurrency(last_invoice.amount)} on{' '}
                                    {formatShortMonthDay(last_invoice.paid_date ?? last_invoice.bill_date)}
                                    {nextSummary ? ` · ${nextSummary}` : ''}
                                </Text>
                            </Stack>
                        </Row>
                        <Link onClick={onViewInvoice} color={Colors.success}>
                            View invoice
                        </Link>
                    </Row>
                </Card>
            )}

            <Grid columns={4} collapseAt="md" collapsedColumns={2} gap="md">
                <StatCard
                    icon={amount_due > 0 ? ErrorOutline : CheckCircleOutline}
                    bgColor={amount_due > 0 ? Colors.warningBackground : Colors.secondarySoft}
                    iconColor={amount_due > 0 ? Colors.warning : Colors.secondary}
                    value={formatCurrency(amount_due)}
                    valueTone={amount_due > 0 ? 'warning' : undefined}
                    label="Amount due"
                />
                <StatCard
                    icon={ReceiptLongOutlined}
                    value={
                        isManualPayer
                            ? last_payment
                                ? formatCurrency(last_payment.amount)
                                : '—'
                            : last_invoice
                              ? formatCurrency(last_invoice.amount)
                              : '—'
                    }
                    label={
                        isManualPayer
                            ? last_payment?.paid_date
                                ? `Last payment · ${formatShortMonthDay(last_payment.paid_date)}`
                                : 'Last payment'
                            : 'Last invoice'
                    }
                />
                <StatCard
                    icon={ScheduleOutlined}
                    value={nextShort}
                    label={isManualPayer ? 'Next due' : 'Charge date'}
                    bgColor={Colors.accentSoft}
                    iconColor={Colors.accent}
                />
                <StatCard
                    icon={CreditCardOutlined}
                    value={
                        isManualPayer
                            ? next_due_date
                                ? formatCurrency(next_due_amount)
                                : '—'
                            : has_upcoming_charge
                              ? formatCurrency(upcoming_charge_amount)
                              : '—'
                    }
                    label={isManualPayer ? 'Next payment' : 'Next charge'}
                />
            </Grid>
        </Stack>
    );
};

interface StatCardProps {
    icon: SvgIconComponent;
    value: string | number;
    label: string;
    bgColor?: string;
    iconColor?: string;
    valueTone?: 'warning';
}

function StatCard({ icon, value, label, bgColor, iconColor, valueTone }: StatCardProps) {
    return (
        <Card padding="md">
            <Row gap="md" align="center">
                <Icon icon={icon} size="md" bgColor={bgColor} iconColor={iconColor} />
                <Stack gap="none">
                    <Text size="xl" weight="bold" tone={valueTone}>
                        {value}
                    </Text>
                    <Text size="sm" tone="secondary">
                        {label}
                    </Text>
                </Stack>
            </Row>
        </Card>
    );
}

export default PaymentsSummary;
